import { Button, IconOutlook, Input, Label, Skeleton } from '@folio/components';
import { useQuery } from '@tanstack/react-query';
import {
  emailAccountControllerListMineQueryOptions,
  useEmailAccountControllerConnect,
} from 'korint-sdk';
import { ChevronRight, Loader2, User, Users } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { DrawerPanel } from './DrawerPanel';
import { ProviderLogo } from './ProviderLogo';

type Step = { name: 'type' } | { name: 'provider'; isShared: boolean } | { name: 'sharedMailbox' };

type Provider = 'gmail' | 'outlook';

function ChoiceCard({
  icon,
  label,
  description,
  loading = false,
  disabled = false,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  description: string;
  loading?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:border-primary hover:bg-primary/5 disabled:pointer-events-none disabled:opacity-60"
    >
      <span
        aria-hidden
        className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10"
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <Text inline size="sm" weight="semibold" className="block">
          {label}
        </Text>
        <Text inline size="xs" tone="muted" className="block">
          {description}
        </Text>
      </span>
      {loading ? (
        <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
      ) : (
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      )}
    </button>
  );
}

export function ConnectFunnel({ onExit }: { onExit: () => void }) {
  const { t } = useTranslation();
  const { data, status } = useQuery(emailAccountControllerListMineQueryOptions());
  const canManageSharedAccounts = data?.canManageSharedAccounts ?? false;

  const [requestedStep, setRequestedStep] = useState<Step>({ name: 'type' });
  // Only admins may connect a shared mailbox, so everyone else skips the type question.
  const step: Step =
    requestedStep.name === 'type' && !canManageSharedAccounts
      ? { name: 'provider', isShared: false }
      : requestedStep;

  const goBack = () => {
    if (step.name === 'sharedMailbox') setRequestedStep({ name: 'provider', isShared: true });
    else if (step.name === 'provider' && canManageSharedAccounts)
      setRequestedStep({ name: 'type' });
    else onExit();
  };

  const { mutateAsync: connect } = useEmailAccountControllerConnect();
  const [connecting, setConnecting] = useState<Provider | 'sharedMailbox' | null>(null);
  const [sharedMailboxAddress, setSharedMailboxAddress] = useState('');
  const sharedMailboxInputId = useId();

  const startConsent = async ({
    provider,
    isShared,
    delegatedMailboxAddress,
  }: {
    provider: Provider;
    isShared: boolean;
    delegatedMailboxAddress?: string;
  }) => {
    setConnecting(delegatedMailboxAddress ? 'sharedMailbox' : provider);
    try {
      const { url } = await connect({
        data: {
          provider,
          returnUrl: window.location.origin + window.location.pathname,
          isShared,
          ...(delegatedMailboxAddress && { delegatedMailboxAddress }),
        },
      });
      // Keep the spinner up: the full-page redirect unloads the component, so never reset here.
      window.location.assign(url);
    } catch {
      setConnecting(null);
    }
  };

  // Admins must not skip the type step while the permission is still loading
  if (!data) {
    return (
      <DrawerPanel title={t('connectedEmailAccounts.connect')} onBack={onExit}>
        {status === 'error' ? (
          <Text size="sm" tone="muted">
            {t('connectedEmailAccounts.error')}
          </Text>
        ) : (
          <Stack gap={3}>
            {Array.from({ length: 2 }, (_, index) => (
              <Skeleton key={index} className="h-16 w-full rounded-lg" />
            ))}
          </Stack>
        )}
      </DrawerPanel>
    );
  }

  return (
    <DrawerPanel
      title={t('connectedEmailAccounts.connect')}
      onBack={goBack}
      isBackDisabled={connecting !== null}
    >
      {step.name === 'type' && (
        <Stack gap={3}>
          <Text size="sm" tone="muted">
            {t('connectedEmailAccounts.funnel.type.question')}
          </Text>
          {(['mine', 'shared'] as const).map(type => (
            <ChoiceCard
              key={type}
              icon={
                type === 'mine' ? (
                  <User className="size-4 text-primary" />
                ) : (
                  <Users className="size-4 text-primary" />
                )
              }
              label={t(`connectedEmailAccounts.groups.${type}.label`)}
              description={t(`connectedEmailAccounts.funnel.type.${type}`)}
              onClick={() => setRequestedStep({ name: 'provider', isShared: type === 'shared' })}
            />
          ))}
        </Stack>
      )}

      {step.name === 'provider' && (
        <Stack gap={3}>
          <Text size="sm" tone="muted">
            {t(
              step.isShared
                ? 'connectedEmailAccounts.funnel.provider.sharedQuestion'
                : 'connectedEmailAccounts.funnel.provider.question',
            )}
          </Text>
          {(['gmail', 'outlook'] as const).map(provider => (
            <ChoiceCard
              key={provider}
              icon={<ProviderLogo type={provider} />}
              label={t(`connectedEmailAccounts.funnel.provider.${provider}.label`)}
              description={t(`connectedEmailAccounts.funnel.provider.${provider}.description`)}
              loading={connecting === provider}
              disabled={connecting !== null}
              onClick={() => startConsent({ provider, isShared: step.isShared })}
            />
          ))}
          {step.isShared && (
            <ChoiceCard
              icon={<ProviderLogo type="outlook" />}
              label={t('connectedEmailAccounts.funnel.provider.sharedMailbox.label')}
              description={t('connectedEmailAccounts.funnel.provider.sharedMailbox.description')}
              disabled={connecting !== null}
              onClick={() => setRequestedStep({ name: 'sharedMailbox' })}
            />
          )}
        </Stack>
      )}

      {step.name === 'sharedMailbox' && (
        <Stack asChild gap={4}>
          <form
            onSubmit={event => {
              event.preventDefault();
              startConsent({
                provider: 'outlook',
                isShared: true,
                delegatedMailboxAddress: sharedMailboxAddress.trim(),
              });
            }}
          >
            <Text size="sm" tone="muted">
              {t('connectedEmailAccounts.funnel.sharedMailbox.description')}
            </Text>
            <Stack gap={2}>
              <Label htmlFor={sharedMailboxInputId}>
                {t('connectedEmailAccounts.funnel.sharedMailbox.addressLabel')}
              </Label>
              <Input
                id={sharedMailboxInputId}
                type="email"
                required
                autoFocus
                autoComplete="off"
                placeholder={t('connectedEmailAccounts.funnel.sharedMailbox.addressPlaceholder')}
                value={sharedMailboxAddress}
                onChange={event => setSharedMailboxAddress(event.target.value)}
              />
            </Stack>
            <Button
              type="submit"
              size="sm"
              leftIcon={<IconOutlook className="size-4" />}
              loading={connecting === 'sharedMailbox'}
              disabled={connecting !== null}
              className="self-start"
            >
              {t('connectedEmailAccounts.funnel.sharedMailbox.continue')}
            </Button>
          </form>
        </Stack>
      )}
    </DrawerPanel>
  );
}
